import * as React from "react";

import * as Sentry from "@sentry/react";
import { FullScreenLoader } from "@v2/components/FullScreenLoader";
import {
  hasRecentlyReloadedForChunkLoad,
  isChunkLoadError,
  markChunkLoadReloaded,
} from "replo-utils/lib/chunkLoadError";

// NOTE (Noah, 2026-07-20): After a frontend deploy, editor tabs that are
// already open still reference the previous build's content-hashed route
// chunks. The first lazy navigation (e.g. switching projects) then tries to
// fetch a now-deleted chunk, and because React.lazy has no retry the rejected
// import crashes the whole app into the top-level Sentry crash dialog
// (EDITOR-FRONTEND-7YS). We detect that specific failure, show the branded
// loader, and do a one-time full reload to pull the fresh bundle onto the same
// URL, guarded against reload loops so a persistent failure still surfaces.

type ChunkLoadErrorBoundaryState = {
  error: unknown;
  isChunkLoadError: boolean;
};

class ChunkLoadErrorBoundary extends React.Component<
  React.PropsWithChildren,
  ChunkLoadErrorBoundaryState
> {
  state: ChunkLoadErrorBoundaryState = {
    error: null,
    isChunkLoadError: false,
  };

  private hasTriggeredReload = false;

  static getDerivedStateFromError(error: unknown): ChunkLoadErrorBoundaryState {
    return { error, isChunkLoadError: isChunkLoadError(error) };
  }

  componentDidCatch() {
    if (
      this.hasTriggeredReload ||
      !this.state.isChunkLoadError ||
      hasRecentlyReloadedForChunkLoad()
    ) {
      return;
    }
    this.hasTriggeredReload = true;
    markChunkLoadReloaded();
    Sentry.addBreadcrumb({
      category: "chunk-load",
      message: "Recovered from stale chunk load error via full reload",
      level: "info",
    });
    // NOTE (Noah, 2026-07-20): Defer a frame so the loader paints before the
    // reload tears the page down.
    window.setTimeout(() => window.location.reload(), 150);
  }

  render() {
    const { error, isChunkLoadError: isChunk } = this.state;
    if (error) {
      // NOTE (Noah, 2026-07-20): Only stale-chunk failures are recoverable by
      // reloading. Re-throw everything else — and chunk errors a reload already
      // failed to fix — so the outer SentryBoundary reports and shows its
      // crash dialog as before.
      if (isChunk && !hasRecentlyReloadedForChunkLoad()) {
        return <FullScreenLoader />;
      }
      throw error;
    }
    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
